import Joi from '@hapi/joi';
import {
  RunNow,
  Schedule,
  OnEvent,
  Wait,
  Condition,
  ConditionalWait,
  Filter,
  End,
  AssignPlaybook,
  CreateTask,
  SetAttribute,
  SendEmail,
  SlackLogo,
  TeamsLogo,
} from '../../components/ScenarioIcons';
import { Pipelines } from '../../components/DuoTone';
import constants, {
  DATA_TYPES,
  SPECIAL_USER_FIELD_IDS,
} from './CommonConstants';
import ButtonEdge from '../../pages/Scenarios/CreateEdit/ButtonEdge/ButtonEdge';
import { SYSTEM_FIELDS } from './SystemFieldConstants';

const { ORGANIZATION_LEVEL, ACCOUNT_LEVEL, CONTACT_LEVEL } = constants;

export const PLACEHOLDER_NODE_ID = 'initial-0';
export const SCENARIO_LEVEL_KEY = 'SCENARIO_LEVEL_KEY';
export const MY_LEVEL_SCENARIO = 'myLevel';
export const ALL_LEVEL_SCENARIO = 'allLevel';

export const SCENARIO_LEVEL = {
  ORGANIZATION: ORGANIZATION_LEVEL,
  ACCOUNT: ACCOUNT_LEVEL,
  CONTACT: CONTACT_LEVEL,
};

export const SCENARIO_STATUS = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived',
  PAUSED: 'paused',
};

export const SCENARIO_STATUS_MAPPING = {
  [SCENARIO_STATUS.ACTIVE]: { color: 'green', displayName: 'Active' },
  [SCENARIO_STATUS.DRAFT]: { color: 'blue', displayName: 'Draft' },
  [SCENARIO_STATUS.INACTIVE]: { color: 'yellow', displayName: 'Inactive' },
  [SCENARIO_STATUS.ARCHIVED]: { color: 'grey', displayName: 'Archived' },
  [SCENARIO_STATUS.PAUSED]: { color: 'purple', displayName: 'Paused' },
};

export const SCENARIO_NODE_CATEGORY = {
  TRIGGER: 'trigger',
  OPERATOR: 'operator',
  ACTION: 'action',
};

export const SCENARIO_NODE_NAME = {
  schedule_now: 'schedule_now',
  schedule_later: 'schedule_later',
  on_event: 'on_event',
  wait: 'wait',
  conditional_wait: 'conditional_wait',
  condition: 'condition',
  filter: 'filter',
  end: 'end',
  assign_playbook: 'assign_playbook',
  create_task: 'create_task',
  set_attribute: 'set_attribute',
  send_email: 'send_email',
  slack_message: 'slack_message',
  create_pipeline: 'create_pipeline',
  teams_message: 'teams_message',
  churn_entity: 'churn_entity',
};

export const SCENARIO_NODE_TYPE = {
  [SCENARIO_NODE_NAME.schedule_now]: {
    name: SCENARIO_NODE_NAME.schedule_now,
    displayName: 'Run Now',
    category: SCENARIO_NODE_CATEGORY.TRIGGER,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.schedule_later]: {
    name: SCENARIO_NODE_NAME.schedule_later,
    displayName: 'Schedule',
    category: SCENARIO_NODE_CATEGORY.TRIGGER,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.on_event]: {
    name: SCENARIO_NODE_NAME.on_event,
    displayName: 'On Event',
    category: SCENARIO_NODE_CATEGORY.TRIGGER,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.wait]: {
    name: SCENARIO_NODE_NAME.wait,
    displayName: 'Wait',
    category: SCENARIO_NODE_CATEGORY.OPERATOR,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.conditional_wait]: {
    name: SCENARIO_NODE_NAME.conditional_wait,
    displayName: 'Conditional Wait',
    category: SCENARIO_NODE_CATEGORY.OPERATOR,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.condition]: {
    name: SCENARIO_NODE_NAME.condition,
    displayName: 'Condition',
    category: SCENARIO_NODE_CATEGORY.OPERATOR,
    branchingFactor: 2,
  },
  [SCENARIO_NODE_NAME.filter]: {
    name: SCENARIO_NODE_NAME.filter,
    displayName: 'Filter',
    category: SCENARIO_NODE_CATEGORY.OPERATOR,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.end]: {
    name: SCENARIO_NODE_NAME.end,
    displayName: 'End',
    category: SCENARIO_NODE_CATEGORY.OPERATOR,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.assign_playbook]: {
    name: SCENARIO_NODE_NAME.assign_playbook,
    displayName: 'Assign Playbook',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.create_task]: {
    name: SCENARIO_NODE_NAME.create_task,
    displayName: 'Create Task',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.set_attribute]: {
    name: SCENARIO_NODE_NAME.set_attribute,
    displayName: 'Set Attribute',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.send_email]: {
    name: SCENARIO_NODE_NAME.send_email,
    displayName: 'Send Email',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.slack_message]: {
    name: SCENARIO_NODE_NAME.slack_message,
    displayName: 'Slack Message',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.create_pipeline]: {
    name: SCENARIO_NODE_NAME.create_pipeline,
    displayName: 'Create Pipeline',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.teams_message]: {
    name: SCENARIO_NODE_NAME.teams_message,
    displayName: 'MS Teams',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
  [SCENARIO_NODE_NAME.churn_entity]: {
    name: SCENARIO_NODE_NAME.churn_entity,
    displayName: 'Churn Entity',
    category: SCENARIO_NODE_CATEGORY.ACTION,
    branchingFactor: 1,
  },
};

export const BUILDER_ICON_MAP = {
  [SCENARIO_NODE_NAME.schedule_now]: RunNow,
  [SCENARIO_NODE_NAME.schedule_later]: Schedule,
  [SCENARIO_NODE_NAME.on_event]: OnEvent,
  [SCENARIO_NODE_NAME.wait]: Wait,
  [SCENARIO_NODE_NAME.conditional_wait]: ConditionalWait,
  [SCENARIO_NODE_NAME.condition]: Condition,
  [SCENARIO_NODE_NAME.filter]: Filter,
  [SCENARIO_NODE_NAME.end]: End,
  [SCENARIO_NODE_NAME.assign_playbook]: AssignPlaybook,
  [SCENARIO_NODE_NAME.create_task]: CreateTask,
  [SCENARIO_NODE_NAME.set_attribute]: SetAttribute,
  [SCENARIO_NODE_NAME.send_email]: SendEmail,
  [SCENARIO_NODE_NAME.slack_message]: SlackLogo,
  [SCENARIO_NODE_NAME.create_pipeline]: Pipelines,
  [SCENARIO_NODE_NAME.teams_message]: TeamsLogo,
  [SCENARIO_NODE_NAME.churn_entity]: SetAttribute,
};

export const COMING_SOON_LIST = [SCENARIO_NODE_NAME.churn_entity];

export const SCHEDULE_TYPE = {
  SCHEDULE_NOW: 'schedule_now', // TODO: remove
  SCHEDULE_LATER: 'schedule',
};

const CANVAS_NODE_TYPE = {
  DEFAULT: 'default',
  INPUT: 'input',
  OUTPUT: 'output',
};

export const WEEK_DAYS = {
  SUNDAY: 'sunday',
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
};

export const DAYS_OF_THE_WEEK = [
  { displayName: 'Sunday', id: WEEK_DAYS.SUNDAY, displayLetter: 'S', index: 1 },
  { displayName: 'Monday', id: WEEK_DAYS.MONDAY, displayLetter: 'M', index: 2 },
  {
    displayName: 'Tuesday',
    id: WEEK_DAYS.TUESDAY,
    displayLetter: 'T',
    index: 3,
  },
  {
    displayName: 'Wednesday',
    id: WEEK_DAYS.WEDNESDAY,
    displayLetter: 'W',
    index: 4,
  },
  {
    displayName: 'Thursday',
    id: WEEK_DAYS.THURSDAY,
    displayLetter: 'T',
    index: 5,
  },
  { displayName: 'Friday', id: WEEK_DAYS.FRIDAY, displayLetter: 'F', index: 6 },
  {
    displayName: 'Saturday',
    id: WEEK_DAYS.SATURDAY,
    displayLetter: 'S',
    index: 7,
  },
];

export const WEEK_DAY_TO_NUMBER = {
  [WEEK_DAYS.SUNDAY]: 0,
  [WEEK_DAYS.MONDAY]: 1,
  [WEEK_DAYS.TUESDAY]: 2,
  [WEEK_DAYS.WEDNESDAY]: 3,
  [WEEK_DAYS.THURSDAY]: 4,
  [WEEK_DAYS.FRIDAY]: 5,
  [WEEK_DAYS.SATURDAY]: 6,
};

export const MONTHLY_OPTIONS = {
  DAY: 'day',
  WEEK: 'week',
  LAST_DAY: 'last_day',
};

export const SCENARIO_NODE_EDGE_POINT_TYPE = {
  [SCENARIO_NODE_NAME.schedule_now]: CANVAS_NODE_TYPE.INPUT,
  [SCENARIO_NODE_NAME.schedule_later]: CANVAS_NODE_TYPE.INPUT,
  [SCENARIO_NODE_NAME.on_event]: CANVAS_NODE_TYPE.INPUT,
  [SCENARIO_NODE_NAME.wait]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.conditional_wait]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.condition]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.filter]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.end]: CANVAS_NODE_TYPE.OUTPUT,
  [SCENARIO_NODE_NAME.assign_playbook]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.create_task]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.set_attribute]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.send_email]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.slack_message]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.create_pipeline]: CANVAS_NODE_TYPE.DEFAULT,
  [SCENARIO_NODE_NAME.teams_message]: CANVAS_NODE_TYPE.DEFAULT,
};

/**
 * Add node name into the corresponding array for disabled the node according to the selected level.
 */
export const DISABLE_NODE_TYPES_FOR_GIVEN_ENTITY_LEVEL = {
  [SCENARIO_LEVEL.ORGANIZATION]: [],
  [SCENARIO_LEVEL.ACCOUNT]: [],
  [SCENARIO_LEVEL.CONTACT]: [
    SCENARIO_NODE_NAME.assign_playbook,
    SCENARIO_NODE_NAME.create_pipeline,
    SCENARIO_NODE_NAME.conditional_wait,
    SCENARIO_NODE_NAME.churn_entity,
  ],
};

export const CANVAS_ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DUPLICATE: 'duplicate',
};

export const BUTTON_EDGE = 'buttonedge';
export const NON_EDITABLE_EDGE = 'smoothstep';

export const CONDITION_YES_LABEL = 'Yes';
export const CONDITION_NO_LABEL = 'No';

export const EDGE_TYPES = {
  [BUTTON_EDGE]: ButtonEdge,
};

export const SCENARIO_SCOPES = {
  LIST_VIEW: 'LIST_VIEW',
  VIEW_CANVAS: 'VIEW_CANVAS',
};

export const nonConfigurableNodes = [
  SCENARIO_NODE_NAME.schedule_now,
  SCENARIO_NODE_NAME.end,
];

export const SCENARIO_ACTIONS = {
  EDIT: 'edit',
  VIEW: 'view',
  ARCHIVE: 'archive',
  DELETE: 'delete',
  ACTIVATE: 'activate',
  DEACTIVATE: 'deactivate',
  RENAME: 'rename',
};

export const SCENARIO_ACTIONS_DISPLAY_NAME = {
  [SCENARIO_ACTIONS.EDIT]: 'Edit',
  [SCENARIO_ACTIONS.VIEW]: 'View',
  [SCENARIO_ACTIONS.ARCHIVE]: 'Archive',
  [SCENARIO_ACTIONS.DELETE]: 'Delete',
  [SCENARIO_ACTIONS.ACTIVATE]: 'Activate',
  [SCENARIO_ACTIONS.DEACTIVATE]: 'Deactivate',
  [SCENARIO_ACTIONS.RENAME]: 'Rename',
};

export const ON_EVENT_TYPE = {
  CREATE_ENTITY: 'create_entity',
  CHANGE_ATTRIBUTE: 'change_attribute',
  SEGMENT_EVENT: 'segment_event',
  CHANGE_STATUS: 'change_status',
};

export const ON_EVENT_SPECIFIC_CHANGE_TYPE = {
  FROM: 'from',
  TO: 'to',
};

export const ON_EVENT_ATTRIBUTE_CHANGE_TYPE = {
  ANY: 'any',
  SPECIFIC: 'specific',
  PERCENTAGE: 'percentage',
};

export const DEVIATION_TYPE = {
  INCREASE: 'increase',
  DECREASE: 'decrease',
  NO_CHANGE: 'no_change',
};

export const PRECISION_TYPE = {
  AT_LEAST: 'at_least',
  AT_MOST: 'at_most',
  EXACTLY: 'exactly',
};

export const PRECISION_TYPE_MAP = {
  [PRECISION_TYPE.AT_LEAST]: 'at least',
  [PRECISION_TYPE.AT_MOST]: 'at most',
  [PRECISION_TYPE.EXACTLY]: 'exactly',
};

export const ATTRIBUTE_CHANGE_OCCURRENCE = {
  IMMEDIATE: 'immediate',
  PREVIOUS: 'previous',
};

export const ON_EVENT_PERIOD = {
  DAYS: 'day',
  WEEKS: 'week',
  MONTHS: 'month',
  QUARTERS: 'quarter',
};

export const SCHEDULE_PERIOD = {
  DAYS: 'day',
  WEEKS: 'week',
  MONTHS: 'month',
  YEARS: 'year',
};

export const SCHEDULE_END_TYPES = {
  NEVER: 'never',
  ON: 'on',
  AFTER: 'after',
};

export const GENERAL_CHECK_STATUS = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
};

export const NODE_CONFIG_MODAL = {
  [SCENARIO_NODE_NAME.on_event]: {
    [ON_EVENT_TYPE.CREATE_ENTITY]: {
      type: Joi.string().required(),
    },
    [ON_EVENT_TYPE.CHANGE_ATTRIBUTE]: {
      attribute: Joi.string().required(),
      changeType: Joi.string().required(),
      changeOccurrence: Joi.when('changeType', {
        is: ON_EVENT_ATTRIBUTE_CHANGE_TYPE.ANY,
        then: Joi.string()
          .valid(...Object.values(ATTRIBUTE_CHANGE_OCCURRENCE))
          .required(),
        otherwise: Joi.forbidden(),
      }),
      changeConfig: Joi.when('changeType', {
        is: ON_EVENT_ATTRIBUTE_CHANGE_TYPE.SPECIFIC,
        then: Joi.object({
          from: Joi.any().required(),
          to: Joi.any().required(),
        }).required(),
        otherwise: Joi.when(Joi.ref('changeOccurrence'), {
          is: ATTRIBUTE_CHANGE_OCCURRENCE.PREVIOUS,
          then: Joi.object({
            timespan: Joi.number().integer().strict().required(),
            period: Joi.string()
              .valid(...Object.values(ON_EVENT_PERIOD))
              .required(),
          }).required(),
          otherwise: Joi.when('changeType', {
            is: ON_EVENT_ATTRIBUTE_CHANGE_TYPE.PERCENTAGE,
            then: Joi.object({
              deviation: Joi.object({
                type: Joi.string()
                  .valid(DEVIATION_TYPE.DECREASE, DEVIATION_TYPE.INCREASE)
                  .required(),
                value: Joi.number().integer().strict().required(),
                precision: Joi.string()
                  .valid(...Object.values(PRECISION_TYPE))
                  .required(),
              }).required(),
            }).required(),
            otherwise: Joi.forbidden(),
          }),
        }),
      }).required(),
      repeat: Joi.object({
        enable: Joi.boolean().required(),
        restrictions: Joi.object({
          time: Joi.object({
            timespan: Joi.number().integer().min(1).strict().required(),
            period: Joi.string()
              .valid(...Object.values(ON_EVENT_PERIOD))
              .required(),
          }).optional(),
          occurrences: Joi.number().integer().min(1).strict().optional(),
        }).optional(),
      }).required(),
    },
    [ON_EVENT_TYPE.SEGMENT_EVENT]: {
      type: Joi.string().required(),
    },
  },
};

export const ON_EVENT_ENTITY_CREATE_OPTIONS = {
  [constants.ORGANIZATION_LEVEL]: {
    entity: 'Organization',
    opportunity: 'Organization Opportunities',
    risk: 'Organization Risk',
  },
  [constants.ACCOUNT_LEVEL]: {
    entity: 'Account',
    opportunity: 'Account Opportunities',
    risk: 'Account Risk',
  },
  [constants.CONTACT_LEVEL]: {
    entity: 'Contact',
  },
};

export const NODE_EXECUTION_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  UNATTENDED_FAILURE: 'unattended_failure',
  ATTENDED_FAILURE: 'attended_failure',
  UNATTENDED_SKIP: 'unattended_skip',
  ATTENDED_SKIP: 'attended_skip',
};

export const NODE_EXECUTION_STATUS_TAG_COLOUR = {
  [NODE_EXECUTION_STATUS.UNATTENDED_FAILURE]: 'red',
  [NODE_EXECUTION_STATUS.UNATTENDED_SKIP]: 'yellow',
  [NODE_EXECUTION_STATUS.REJECTED]: 'purple',
};

export const NODE_EXECUTION_SUMMARY_STATUS = {
  [NODE_EXECUTION_STATUS.UNATTENDED_SKIP]: 'unattendedSkip',
  [NODE_EXECUTION_STATUS.UNATTENDED_FAILURE]: 'unattendedFailure',
};

export const NODE_SKIP_CONFIG_TYPE = {
  EVERY: 'EVERY',
  PERIODIC: 'PERIODIC',
  ONCE: 'ONCE',
};

const SYSTEM_HEALTH_SCORE = {
  systemFieldId: -1,
  name: 'system:healthScore',
  dataType: DATA_TYPES.number,
  displayName: 'Health Score',
};

export const SYSTEM_HEALTH_STATUS = {
  systemFieldId: -2,
  name: 'system:healthStatus',
  dataType: DATA_TYPES.health,
  displayName: 'Health Status',
};

export const ON_EVENT_SYSTEM_FIELDS = {
  [SYSTEM_HEALTH_SCORE.name]: SYSTEM_HEALTH_SCORE,
  [SYSTEM_HEALTH_STATUS.name]: SYSTEM_HEALTH_STATUS,
  [SYSTEM_FIELDS.PULSE]: {
    systemFieldId: -3,
    name: SYSTEM_FIELDS.PULSE,
    displayName: 'Pulse',
    dataType: DATA_TYPES.pulse,
  },
  [SYSTEM_FIELDS.LIFECYCLE_STAGE]: {
    systemFieldId: -4,
    name: SYSTEM_FIELDS.LIFECYCLE_STAGE,
    displayName: 'Lifecycle Stage',
    dataType: DATA_TYPES.lifecycleStage,
  },
  [SYSTEM_FIELDS.OWNER]: {
    systemFieldId: -5,
    name: SYSTEM_FIELDS.OWNER,
    displayName: 'Owner',
    dataType: DATA_TYPES.owner,
  },
  [SYSTEM_FIELDS.EMAIL]: {
    systemFieldId: -6,
    name: SYSTEM_FIELDS.EMAIL,
    displayName: 'Email',
    dataType: DATA_TYPES.email,
  },
  [SYSTEM_FIELDS.EXTERNAL_ID]: {
    systemFieldId: -7,
    name: SYSTEM_FIELDS.EXTERNAL_ID,
    displayName: 'External ID',
    dataType: DATA_TYPES.string,
  },
  [SYSTEM_FIELDS.LAST_NPS]: {
    systemFieldId: -8,
    name: SYSTEM_FIELDS.LAST_NPS,
    displayName: 'Last NPS Rating',
    dataType: DATA_TYPES.number,
  },
  [SYSTEM_FIELDS.NPS_CATEGORY]: {
    systemFieldId: -9,
    name: SYSTEM_FIELDS.NPS_CATEGORY,
    displayName: 'NPS Category',
    dataType: DATA_TYPES.npsCategory,
  },
  [SYSTEM_FIELDS.PRIMARY_OWNER]: {
    systemFieldId: -10,
    name: SYSTEM_FIELDS.PRIMARY_OWNER,
    displayName: 'Primary Owner',
    dataType: DATA_TYPES.user,
  },
  // CSAT Scenario attribute
  [SYSTEM_FIELDS.LAST_CSAT]: {
    systemFieldId: -11,
    name: SYSTEM_FIELDS.LAST_CSAT,
    displayName: 'Last CSAT Rating',
    dataType: DATA_TYPES.number,
  },
  [SYSTEM_FIELDS.CSAT_CATEGORY]: {
    systemFieldId: -12,
    name: SYSTEM_FIELDS.CSAT_CATEGORY,
    displayName: 'CSAT Category',
    dataType: DATA_TYPES.csatCategory,
  },
};

export const LEVEL_ON_EVENT_SYSTEM_FIELDS_MAPPING = {
  [constants.ORGANIZATION_LEVEL]: {
    [SYSTEM_HEALTH_SCORE.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_SCORE.name]],
    [SYSTEM_HEALTH_STATUS.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_STATUS.name]],
    [SYSTEM_FIELDS.PULSE]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.PULSE],
    // [SYSTEM_FIELDS.OWNER]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.OWNER], // TODO: implement owner input
    [SYSTEM_FIELDS.LIFECYCLE_STAGE]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.LIFECYCLE_STAGE],
    [SYSTEM_FIELDS.EXTERNAL_ID]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.EXTERNAL_ID],
    [SYSTEM_FIELDS.PRIMARY_OWNER]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.PRIMARY_OWNER],
  },
  [constants.ACCOUNT_LEVEL]: {
    [SYSTEM_HEALTH_SCORE.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_SCORE.name]],
    [SYSTEM_HEALTH_STATUS.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_STATUS.name]],
    [SYSTEM_FIELDS.PULSE]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.PULSE],
    // [SYSTEM_FIELDS.OWNER]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.OWNER], // TODO: implement owner input
    [SYSTEM_FIELDS.LIFECYCLE_STAGE]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.LIFECYCLE_STAGE],
    [SYSTEM_FIELDS.EXTERNAL_ID]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.EXTERNAL_ID],
    [SYSTEM_FIELDS.PRIMARY_OWNER]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.PRIMARY_OWNER],
  },
  [constants.CONTACT_LEVEL]: {
    [SYSTEM_HEALTH_SCORE.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_SCORE.name]],
    [SYSTEM_HEALTH_STATUS.name]:
      ON_EVENT_SYSTEM_FIELDS[[SYSTEM_HEALTH_STATUS.name]],
    [SYSTEM_FIELDS.PULSE]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.PULSE],
    [SYSTEM_FIELDS.EMAIL]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.EMAIL],
    [SYSTEM_FIELDS.LIFECYCLE_STAGE]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.LIFECYCLE_STAGE],
    [SYSTEM_FIELDS.EXTERNAL_ID]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.EXTERNAL_ID],
    [SYSTEM_FIELDS.LAST_NPS]: ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.LAST_NPS],
    [SYSTEM_FIELDS.NPS_CATEGORY]:
      ON_EVENT_SYSTEM_FIELDS[SYSTEM_FIELDS.NPS_CATEGORY],
  },
};

export const SCENARIO_DURATION_PERIODS = {
  MINUTE: 'minute',
  HOUR: 'hour',
  DAY: 'day',
  CALENDAR_WEEK: 'calendarWeek',
  CALENDAR_MONTH: 'calendarMonth',
};

export const SCENARIO_DURATION_PERIODS_MAPPING = {
  [SCENARIO_DURATION_PERIODS.MINUTE]: {
    value: 'minute',
    displayName: 'Minute(s)',
  },
  [SCENARIO_DURATION_PERIODS.HOUR]: {
    value: 'hour',
    displayName: 'Hour(s)',
  },
  [SCENARIO_DURATION_PERIODS.DAY]: {
    value: 'day',
    displayName: 'Day(s)',
  },
  [SCENARIO_DURATION_PERIODS.CALENDAR_WEEK]: {
    value: 'calendarWeek',
    displayName: 'Calendar Week(s)',
  },
  [SCENARIO_DURATION_PERIODS.CALENDAR_MONTH]: {
    value: 'calendarMonth',
    displayName: 'Calendar Month(s)',
  },
};

export const REPEAT_DURATION_PERIODS = {
  HOUR: 'hour',
  DAYS: 'day',
  WEEKS: 'week',
  MONTHS: 'month',
};

export const REPEAT_DURATION_PERIODS_MAPPING = {
  [REPEAT_DURATION_PERIODS.HOUR]: {
    value: 'hour',
    displayName: 'Hour(s)',
  },
  [REPEAT_DURATION_PERIODS.DAY]: {
    value: 'day',
    displayName: 'Day(s)',
  },
  [REPEAT_DURATION_PERIODS.WEEKS]: {
    value: 'week',
    displayName: 'Week(s)',
  },
  [REPEAT_DURATION_PERIODS.MONTHS]: {
    value: 'month',
    displayName: 'Month(s)',
  },
};

export const EMAIL_SERVICE_TYPES = {
  SES: 'SES',
  INTEGRATED_EMAIL: 'INTEGRATED_EMAIL',
};

export const EMAIL_SERVICE_TYPE_DISPLAY = {
  [EMAIL_SERVICE_TYPES.SES]: 'Bulk Email Service',
  [EMAIL_SERVICE_TYPES.INTEGRATED_EMAIL]: 'Integrated Email Service',
};

export const EMAIL_TYPES = {
  BASIC: 'BASIC',
  TEMPLATE: 'TEMPLATE',
};

export const PERIOD_DEFAULT_MINUTES = [15, 30, 45];

export const SET_ATTRIBUTE_TYPES = {
  VALUE: 'VALUE',
  SCENARIO_REFERENCE: 'SCENARIO_REFERENCE',
  REFERENCE: 'REFERENCE',
  RANDOM: 'RANDOM',
  REFERENCE_ALL: 'REFERENCE_ALL',
};

export const SET_ATTRIBUTE_METHODS = {
  VALUE: 'value',
  REFERENCE: 'reference',
  RANDOM: 'random',
  RELATIVE: 'relative',
};

export const SET_ATTRIBUTE_USER_FIELDS_ID_MAPPING = {
  [SPECIAL_USER_FIELD_IDS.PRIMARY_OWNER]: {
    customColumnId: -1,
    name: 'system:primaryOwner',
    displayName: 'Primary Owner',
  },
};

export const ENTITY_STATUS_TYPES = {
  CHURN: 'churn',
  ARCHIVE: 'archive',
  UNCHURN: 'unchurn',
  UNARCHIVE: 'unarchive',
};

export const ENTITY_STATUS_DISPLAY_NAMES = {
  [ENTITY_STATUS_TYPES.CHURN]: 'When Churned',
  [ENTITY_STATUS_TYPES.ARCHIVE]: 'When Archived',
  [ENTITY_STATUS_TYPES.UNCHURN]: 'When Unchurned',
  [ENTITY_STATUS_TYPES.UNARCHIVE]: 'When Unarchived (Restored)',
};

export const SCENARIO_CONDITIONAL_WAIT_REFERENCES = {
  PLAYBOOK: 'PLAYBOOK',
  TASK: 'TASK',
};

export const SCENARIO_CONDITIONAL_WAIT_ACTIONS = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  ARCHIVED: 'ARCHIVED',
};

export const SCENARIO_SEND_EMAIL_ADDITIONAL_ATTRIBUTES = [
  {
    name: 'signature',
    displayName: 'System Generated Signature',
    message:
      'This signature has been automatically generated based on the details provided in the user profile',
  },
  {
    name: 'user_signature',
    displayName: 'User Signature',
    message:
      'This is the user-defined signature and can be configured in the profile settings',
  },
];
